import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';

/**
 * Wraps a wide table that scrolls horizontally and adds a *second* scrollbar
 * along the top, kept in sync with the real one. The top scrollbar is
 * `position: sticky` so it follows the viewport as the user scrolls the page
 * down — they can scroll horizontally from anywhere in the table without
 * first scrolling all the way down to find the bottom scrollbar.
 *
 * Usage:
 *   <ScrollSyncWrapper>
 *     <table>...</table>
 *   </ScrollSyncWrapper>
 */
const ScrollSyncWrapper = ({ children, topOffset = 0 }) => {
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const [innerWidth, setInnerWidth] = useState(0);
  const syncingRef = useRef(false);

  // Track the width of the scrollable content so the phantom div inside the
  // top scrollbar can match.
  useLayoutEffect(() => {
    const el = bottomRef.current;
    if (!el) return undefined;
    const update = () => setInnerWidth(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Also re-measure when the table inside resizes.
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, []);

  // Hide the top phantom scrollbar entirely when the content fits without
  // overflow — no point taking up vertical space.
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;
    setOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, [innerWidth]);

  const onTopScroll = (e) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (bottomRef.current) bottomRef.current.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };
  const onBottomScroll = (e) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (topRef.current) topRef.current.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  return (
    <>
      <div
        ref={topRef}
        onScroll={onTopScroll}
        className="overflow-x-auto sticky z-20 bg-white"
        style={{
          top: topOffset,
          height: overflowing ? 14 : 0,
          visibility: overflowing ? 'visible' : 'hidden',
        }}
        data-testid="scroll-sync-top"
      >
        <div style={{ width: innerWidth || 1, height: 1 }} />
      </div>
      <div
        ref={bottomRef}
        onScroll={onBottomScroll}
        className="overflow-x-auto"
        data-testid="scroll-sync-body"
      >
        {children}
      </div>
    </>
  );
};

export default ScrollSyncWrapper;
